import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

import * as models from './models'

/**
 * Basic number input.
 * It can be used as number, currency or percent input.
 *
 * **Inputs**
 * - {@link AtnDsNumberComponent.label|label}?
 * - {@link AtnDsNumberComponent._disabled|disabled}?
 * - {@link AtnDsNumberComponent._value|value}?
 * - {@link AtnDsNumberComponent._loading|loading}?
 * - {@link AtnDsNumberComponent._config|config}?
 * - {@link AtnDsNumberComponent._type|type}?
 * - {@link AtnDsNumberComponent._placeholder|placeholder}?
 *
 * **Outputs**
 * - {@link AtnDsNumberComponent.change|change}
 *
 * @example
 * ```html
 *  <!-- Number input, when we don't specify type, we will get number type by default -->
 * <atn-ds-number
 *  [label]="'Number Input'"
 *  [disabled]="false"
 *  [value]="inputValue"
 *  [loading]="false"
 *  [config]="config"
 *  (change)="handleChange($event)"/>
 *
 * <!-- Currency input -->
 * <atn-ds-number
 *  [label]="'Currency Input'"
 *  [disabled]="false"
 *  [value]="inputValue"
 *  [loading]="false"
 *  type="Input.Number.Models.Type.Currency"
 *  [config]="config"
 *  (change)="handleChange($event)"/>
 *
 * <!-- Percent input -->
 * <atn-ds-number
 *  [label]="'Percent Input'"
 *  [disabled]="false"
 *  [value]="inputValue"
 *  [loading]="false"
 *  type="Input.Number.Models.Type.Currency"
 *  [config]="config"
 *  (change)="handleChange($event)"/>
 */
@ngCore.Component({
  imports: [ngCommon.NgIf],
  providers: [ngCommon.PercentPipe],
  selector: 'atn-ds-number',
  standalone: true,
  styleUrls: ['./number.component.scss'],
  templateUrl: './number.component.html',
})
export class AtnDsNumberComponent {
  private readonly _percentPipe: ngCommon.PercentPipe = ngCore.inject(
    ngCommon.PercentPipe,
  )

  /**
   * Label to display above the input
   */
  public labelSg = ngCore.input<string | undefined>('', { alias: 'label' })

  /**
   * Whether the input is disabled
   */
  public disabledSg = ngCore.input<boolean | undefined>(false, {
    alias: 'disabled',
  })

  /**
   * Input value
   * TODO: Convert input to signal. Currently it is dependent from
   * other input and relies that inputs are ordered specifically (`type` input
   * should be before `value`)
   */
  // TODO: Come back to this
  @ngCore.Input()
  public set value(value: number | undefined) {
    this.valueInner =
      this.typeSg() === ents.core.common.value.types.Intrinsic.Percent
        ? this._getPercentDisplayValue(value)
        : value
  }

  public readonly loadingSg = ngCore.input<boolean | undefined>(false, {
    alias: 'loading',
  })

  /**
   * Configuration check {@link models.Config|Config}
   */
  @ngCore.Input()
  public set config(config: Readonly<models.Config>) {
    config = {
      ...this.configSg(),
      ...config,
    }
    this.configSg.set(config)
  }
  public valueInner: number | undefined | null = null

  public readonly configSg: ngCore.WritableSignal<models.Config> =
    ngCore.signal({
      precision: 2,
    })

  // TODO: Try to implement something like this if possible
  // public configSg = ngCore.input<models.Config, models.Config>(
  //   { precision: 2 },
  //   {
  //     alias: 'config',
  //     transform: config => ({ ...this.configSg(), ...config }),
  //   },
  // )

  /**
   * Type of input {@link models.Type|Type}
   *
   * default {@link models.Type.Number|Number}
   */
  public readonly typeSg = ngCore.input<
    ents.core.common.value.types.Intrinsic.ForNumber | undefined
  >(ents.core.common.value.types.Intrinsic.Integer, { alias: 'type' })
  /**
   * Placeholder text input
   */
  public placeholderSg = ngCore.input('', { alias: 'placeholder' })

  public readonly min = ngCore.computed(() =>
    this.configSg().min !== undefined ? this.configSg().min : -Infinity,
  )
  public readonly max = ngCore.computed(() =>
    this.configSg()?.max !== undefined ? this.configSg().max : Infinity,
  )
  public readonly suffix = ngCore.computed(
    (type = this.typeSg(), config = this.configSg()) =>
      config?.suffix ?? (type ? models.Type.getSuffix(type) : undefined),
  )
  public readonly prefix = ngCore.computed(
    (type = this.typeSg(), config = this.configSg()) =>
      config?.prefix ?? (type ? models.Type.getPrefix(type) : undefined),
  )

  public readonly change = ngCore.output<number | undefined>()

  public onChangeHandler(event: Readonly.Deep<Event>): void {
    event.stopPropagation()
    const target = event.target as HTMLInputElement | null
    const tempValue = target?.value ?? ''
    let converted: number | null = +tempValue
    if (
      tempValue === '' ||
      !TypeChecker.isNumber(converted) ||
      isNaN(converted)
    ) {
      converted = null
    }
    const max = this.max()
    const min = this.min()

    if (typeof converted === 'number' && max !== undefined && converted > max) {
      converted = max
    }
    if (typeof converted === 'number' && min !== undefined && converted < min) {
      converted = min
    }
    this.valueInner = converted

    if (
      typeof converted === 'number' &&
      this.typeSg() === ents.core.common.value.types.Intrinsic.Percent
    ) {
      converted = Number(converted / 100)
    }

    this.change.emit(converted ?? undefined)
  }

  private _getPercentDisplayValue(value: number | undefined): number {
    return Number(
      this._percentPipe
        .transform(value, `1.0-${this.configSg().precision}`)
        ?.split('%')?.[0] ?? 0,
    )
  }
}
